import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './ManualSubscription.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

function ManualSubscription() {
    const [users, setUsers] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        packageType: 'smallBusiness',
        billingPeriod: 'monthly',
        customAmount: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchPackages();
    }, []);

    useEffect(() => {
        if (searchTerm.length >= 3) {
            const timer = setTimeout(() => {
                searchUsers();
            }, 500);
            return () => clearTimeout(timer);
        } else {
            setUsers([]);
        }
    }, [searchTerm]);

    const fetchPackages = async () => {
        try {
            const response = await axios.get(`${BASEURL}billing-packages`);
            if (response.data.success) {
                setPackages(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching packages:', err);
        }
    };

    const searchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const params = new URLSearchParams({
                page: 1,
                limit: 10,
                search: searchTerm
            });

            const response = await axios.get(
                `${BASEURL}admin-user/${userId}?${params}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setUsers(response.data.data.users);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error searching users:', err);
            setLoading(false);
        }
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setSearchTerm('');
        setUsers([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedUser) {
            alert('Please select a user');
            return;
        }

        if (!window.confirm(`Subscribe ${selectedUser.mobile} to ${formData.packageType} (${formData.billingPeriod})?`)) {
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const payload = {
                targetUserId: selectedUser._id,
                packageType: formData.packageType,
                billingPeriod: formData.billingPeriod,
                customAmount: formData.customAmount ? parseInt(formData.customAmount) : undefined
            };

            const response = await axios.post(
                `${BASEURL}superadmin/manual-subscribe/${userId}`,
                payload,
                { headers: { token } }
            );

            if (response.data.success) {
                alert(response.data.message);
                setSelectedUser(null);
                setFormData({
                    packageType: 'smallBusiness',
                    billingPeriod: 'monthly',
                    customAmount: ''
                });
            } else {
                alert(response.data.error || 'Subscription failed');
            }
            setSubmitting(false);
        } catch (err) {
            console.error('Error subscribing user:', err);
            alert(err.response?.data?.error || 'Failed to subscribe user');
            setSubmitting(false);
        }
    };

    const selectedPackage = packages.find(pkg => pkg.packageType === formData.packageType);
    const displayPrice = selectedPackage ? (formData.billingPeriod === 'yearly' ? selectedPackage.pricing.yearly.price : selectedPackage.pricing.monthly.price) : 0;

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1>Manual User Subscription</h1>
                    <p>Manually subscribe users to packages</p>
                </div>

                <div className={styles.card}>
                    <h3>Step 1: Search User</h3>
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search by mobile, username, or email (min 3 characters)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                        {loading && <div className={styles.loading}>Searching...</div>}

                        {users.length > 0 && (
                            <div className={styles.searchResults}>
                                {users.map(user => (
                                    <div
                                        key={user._id}
                                        className={styles.userItem}
                                        onClick={() => handleUserSelect(user)}
                                    >
                                        <div>
                                            <strong>{user.username || 'No Name'}</strong>
                                            <br />
                                            <small>{user.mobile}</small>
                                            {user.email && <small> | {user.email}</small>}
                                        </div>
                                        <div className={styles.userStatus}>
                                            <span className={styles[`status-${user.subscriptionStatus || 'trial'}`]}>
                                                {user.subscriptionStatus || 'trial'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedUser && (
                        <div className={styles.selectedUser}>
                            <h4>Selected User:</h4>
                            <div className={styles.userCard}>
                                <div>
                                    <strong>{selectedUser.username || 'No Name'}</strong>
                                    <br />
                                    Mobile: {selectedUser.mobile}
                                    {selectedUser.email && <><br />Email: {selectedUser.email}</>}
                                    <br />
                                    Current Status: <span className={styles[`status-${selectedUser.subscriptionStatus || 'trial'}`]}>{selectedUser.subscriptionStatus || 'trial'}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedUser(null)}
                                    className={styles.clearBtn}
                                >
                                    Change User
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {selectedUser && (
                    <form onSubmit={handleSubmit} className={styles.card}>
                        <h3>Step 2: Select Package</h3>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label>Package Type</label>
                                <select
                                    value={formData.packageType}
                                    onChange={(e) => setFormData({...formData, packageType: e.target.value})}
                                    className={styles.select}
                                >
                                    {packages.map(pkg => (
                                        <option key={pkg.packageType} value={pkg.packageType}>
                                            {pkg.nameBangla || pkg.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Billing Period</label>
                                <select
                                    value={formData.billingPeriod}
                                    onChange={(e) => setFormData({...formData, billingPeriod: e.target.value})}
                                    className={styles.select}
                                >
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.priceDisplay}>
                            <div>Standard Price:</div>
                            <div className={styles.price}>৳{displayPrice.toLocaleString()}</div>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Custom Amount (Optional)</label>
                            <input
                                type="number"
                                placeholder={`Leave empty for standard price (৳${displayPrice})`}
                                value={formData.customAmount}
                                onChange={(e) => setFormData({...formData, customAmount: e.target.value})}
                                className={styles.input}
                            />
                            <small>Enter custom amount only if you want to override the standard price</small>
                        </div>

                        {formData.customAmount && (
                            <div className={styles.customPriceWarning}>
                                Custom amount will be: ৳{parseInt(formData.customAmount).toLocaleString()}
                            </div>
                        )}

                        <div className={styles.formActions}>
                            <button
                                type="submit"
                                disabled={submitting}
                                className={styles.submitBtn}
                            >
                                {submitting ? 'Subscribing...' : 'Subscribe User'}
                            </button>
                        </div>
                    </form>
                )}

                {!selectedUser && (
                    <div className={styles.placeholder}>
                        Search and select a user to begin manual subscription
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default ManualSubscription;
